import type { Command } from 'commander';
import { hostname as osHostname } from 'node:os';
import { resolveContext, type ResolvedContext } from '../../lib/global-opts';
import { requestDeviceCode, pollForToken, type OauthRequestOptions } from '../../lib/oauth';
import { loadPersonalCredentials, savePersonalCredentials } from '../../lib/credentials-store';
import { openInBrowser } from '../../lib/browser-open';
import { channelBanner } from '../../lib/channel';

export interface LoginFlags {
	noBrowser?: boolean;
	force?: boolean;
}

/** 可注入的相依，測試用假實作取代真的等待/開瀏覽器/主機名稱。 */
export interface LoginDeps {
	sleep?: (ms: number) => Promise<void>;
	openBrowser?: (url: string) => boolean;
	hostname?: () => string;
}

/** 已登入時的識別字串；display_name/email 本階段可能皆為空字串（見下方大段註解），故逐層 fallback。 */
function describeIdentity(displayName: string, email: string): string {
	if (displayName && email) return `${displayName} (${email})`;
	if (displayName) return displayName;
	if (email) return email;
	return 'this device';
}

/**
 * `wport login` 核心流程（RFC 8628 device authorization grant）。
 *
 * display_name/email 本階段以空字串存入 credentials —— AS 的 TokenResponse 不含身分資訊，
 * v1 沒有 profile 端點可回填；Task 5（whoami）改走 `GET /oauth/sessions` 的 is_current 撈
 * 裝置名/建立時間做替代身分顯示，這裡先誠實存空值，等 spec B profile 端點落地後再回填。
 */
export async function performLogin(ctx: ResolvedContext, flags: LoginFlags, deps: LoginDeps = {}): Promise<void> {
	const banner = channelBanner();
	if (banner) process.stderr.write(banner);

	const openBrowser = deps.openBrowser ?? openInBrowser;
	const hostnameFn = deps.hostname ?? osHostname;

	if (!flags.force) {
		const existing = loadPersonalCredentials();
		if (existing) {
			process.stdout.write(`Already logged in as ${describeIdentity(existing.display_name, existing.email)}.\n`);
			process.stdout.write('Run `wport login --force` to sign in again.\n');
			return;
		}
	}

	const oauthOpts: OauthRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };
	const device = await requestDeviceCode(oauthOpts, hostnameFn());

	// user_code 是使用者要抄去網頁輸入的一次性代碼，獨立換行、留白讓它在終端機裡醒目好認。
	process.stdout.write(`\nEnter this code when prompted:\n\n  ${device.user_code}\n\n`);
	process.stdout.write(`Visit: ${device.verification_uri}\n`);

	if (!flags.noBrowser && process.stdout.isTTY) {
		const opened = openBrowser(device.verification_uri_complete);
		if (!opened) {
			process.stdout.write(`Open this URL manually: ${device.verification_uri_complete}\n`);
		} else {
			// 即使成功呼叫開瀏覽器的指令也照印網址——SSH 顯示器分離場景下，指令本身可能成功
			// 執行但沒有畫面能顯示瀏覽器，使用者仍需要這行網址才能自己拿去別的裝置開。
			process.stdout.write(`Opened in your browser. If nothing appeared, visit: ${device.verification_uri_complete}\n`);
		}
	}

	const tokens = await pollForToken(oauthOpts, device, deps.sleep);

	const now = Date.now();
	savePersonalCredentials({
		access_token: tokens.access_token,
		refresh_token: tokens.refresh_token,
		expires_at: new Date(now + tokens.expires_in * 1000).toISOString(),
		display_name: '',
		email: '',
		session_created_at: new Date(now).toISOString(),
	});

	process.stdout.write('Logged in.\n');
}

export function registerLoginCommand(program: Command): void {
	program
		.command('login')
		.description('Sign in to your wport personal account (OAuth device authorization flow)')
		.option('--no-browser', 'do not try to open a browser automatically')
		.option('--force', 'sign in again even if already logged in')
		.action(async (opts: { browser?: boolean; force?: boolean }, command: Command) => {
			const ctx = resolveContext(command);
			await performLogin(ctx, { noBrowser: opts.browser === false, force: opts.force });
		});
}
